import React, { forwardRef } from 'react';
import { Alert as ChakraAlert, defineRecipe, useRecipe } from '@chakra-ui/react';
import { CloseButton } from './close-button';

export interface AlertProps extends Omit<ChakraAlert.RootProps, 'title'> {
  startElement?: React.ReactNode;
  endElement?: React.ReactNode;
  title?: React.ReactNode;
  icon?: React.ReactElement;
  closable?: boolean;
  onClose?: () => void;
}

export const alertRecipe = defineRecipe({
  variants: {
    visual: {
      subtle: {
        bg: 'colorPalette.100',
        color: 'colorPalette.50'
      },

      surface: {
        root: {
          bg: 'colorPalette.subtle',
          color: 'colorPalette.fg',
          shadow: 'inset 0 0 0px 1px var(--shadow-color)',
          shadowColor: 'colorPalette.muted'
        },
        indicator: {
          color: 'colorPalette.fg'
        }
      },

      outline: {
        root: {
          color: 'colorPalette.fg',
          shadow: 'inset 0 0 0px 1px var(--shadow-color)',
          shadowColor: 'colorPalette.muted'
        },
        indicator: {
          color: 'colorPalette.fg'
        }
      },

      solid: {
        root: {
          bg: 'colorPalette.solid',
          color: 'colorPalette.contrast'
        },
        indicator: {
          color: 'colorPalette.contrast'
        }
      }
    }
  }
});

export const Alert = forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
  const { title, children, icon, closable, onClose, startElement, endElement, variant, ...rest } = props;
  const recipe = useRecipe({ recipe: alertRecipe });
  const styles = recipe({ visual: variant });
  return (
    <ChakraAlert.Root ref={ref} css={styles} {...rest}>
      {startElement || <ChakraAlert.Indicator>{icon}</ChakraAlert.Indicator>}
      {children ? (
        <ChakraAlert.Content>
          <ChakraAlert.Title>{title}</ChakraAlert.Title>
          <ChakraAlert.Description>{children}</ChakraAlert.Description>
        </ChakraAlert.Content>
      ) : (
        <ChakraAlert.Title flex='1'>{title}</ChakraAlert.Title>
      )}
      {endElement}
      {closable && (
        <CloseButton size='sm' pos='relative' top='-2' insetEnd='-2' alignSelf='flex-start' onClick={onClose} />
      )}
    </ChakraAlert.Root>
  );
});
